/**
 * The record workspace's map canvas — the HOST half of the shared lens (#2276).
 *
 * `@solum/ui`'s `MapLens` draws pins and knows nothing else. Everything that
 * needs the deployment — which rows exist, what a record is called, what its
 * relationship type is called, where the tiles come from — is resolved here,
 * under the caller's own RLS, through doors that already existed.
 *
 * ── NOTHING HERE KNOWS A RELATIONSHIP BY NAME ──────────────────────────────
 * The retired CRM panel read `core.address` directly and hard-coded
 * `located_at`. This component reads the ORDINARY relationship rows every other
 * section reads, and decides what is placeable from the recipe the server
 * resolved for each far entity. An entity that gains coordinates becomes
 * map-capable with no change here.
 *
 * ── AND IT NEVER SUBTRACTS ─────────────────────────────────────────────────
 * Groups whose far entity declares no recipe are simply not fed to it — they
 * keep their ordinary sections beneath the canvas, which `Connections` renders
 * in the map lens exactly as it does in the grid. Rows whose coordinates are
 * missing or invalid go to the lens's own `Location unavailable` list. The only
 * thing that disappears is a pin, and only when there was never a place.
 */
import { useEffect, useMemo, useState } from 'react'
import { MapLens, type MapTileSource } from '@solum/ui'
import type { ConnectionGroup, MapRecipe } from '@solum/experience'
import type { Row } from '@solum/sdk'
import { useRecordTitles } from '../data/queries'
import { useGeoSettings } from './geo'
import { useRelationRows } from './useRelations'
import type { RelationSlot } from './relations'
import { buildMapPins, type MapPinSource } from './mapPins'

export interface RelationMapProps {
  recordId: string
  /** The groups the workspace is showing, in its own order. */
  groups: readonly ConnectionGroup[]
  slotById: ReadonlyMap<string, RelationSlot>
  /**
   * The coordinate recipe per far entity, as the server resolved it.
   *
   * Keyed by entity because a RECIPE is a property of the far entity and of
   * nothing on the edge — that is what keeps every layer of this free of a
   * relationship key. The caller passes only the entities it is drawing.
   */
  recipes: Record<string, MapRecipe | null>
  onOpenRecord?: (entity: string, id: string, name?: string) => void
}

/**
 * The tile seam, from the deployment's OWN setting.
 *
 * `crm/geo.ts` is the governed home for this (crm `2026080402`, #524): the
 * provider moved out of `localStorage` into `core.setting` precisely so it would
 * be admin-gated, audited and maskable, and there is exactly one keyless
 * provider because an embed carries its key in the page and a governed secret
 * cannot. Reading that setting here rather than declaring a second one is the
 * difference between a seam and a fork.
 *
 * `none` returns null, and the lens then draws its explicit "no map provider"
 * state with the records still listed. That is the offline/refused case, said
 * out loud.
 */
function useTileSource(): MapTileSource | null {
  const { mapProvider } = useGeoSettings()
  return useMemo(() => (mapProvider === 'osm'
    ? {
      urlTemplate: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '© OpenStreetMap',
      // The public tile service serves to 19; asking for 20 returns 404s that
      // render as holes in the map rather than as an error anybody can see.
      maxZoom: 19,
    }
    : null), [mapProvider])
}

export function RelationMap({ recordId, groups, slotById, recipes, onOpenRecord }: RelationMapProps) {
  const tiles = useTileSource()

  /** The groups whose far entity actually declares coordinates. */
  const mappable = useMemo(() => groups
    .map((group) => ({ group, slot: slotById.get(group.key) }))
    .filter((pair): pair is { group: ConnectionGroup; slot: RelationSlot } =>
      !!pair.slot && !!recipes[pair.slot.otherEntity]),
    [groups, slotById, recipes])

  /* Rows arrive per SLOT, because `useRelationRows` is a hook and hooks cannot
     be called in a loop. One feed component per mappable slot reports its rows
     up; the canvas renders from the union. That is the same shape
     `RelationSectionBody` uses for the same reason, and it is why the feed
     renders nothing itself. */
  const [rowsBySlot, setRowsBySlot] = useState<Record<string, readonly { record: Row; edge: Row | null }[]>>({})

  const sources = useMemo<MapPinSource[]>(() => mappable.map(({ group, slot }) => ({
    slotId: slot.id,
    groupLabel: group.label,
    targetEntity: slot.otherEntity,
    recipe: recipes[slot.otherEntity] ?? null,
    rows: rowsBySlot[slot.id] ?? [],
  })), [mappable, recipes, rowsBySlot])

  /* Titles come from `core.titles_of` — the platform's answer to "what is this
     record called", declared by the module that owns the entity. It is caller
     RLS, so a record this session cannot name simply has no title, which the
     lens renders as the same sentence its list rows use. */
  const refs = useMemo(() => sources.flatMap((source) => source.rows
    .map((row) => ({ type: source.targetEntity, id: String(row.record.id ?? '') }))
    .filter((ref) => ref.id)), [sources])
  const titles = useRecordTitles(refs)

  const slotOf = useMemo(() => new Map(mappable.map(({ slot }) => [slot.id, slot])), [mappable])

  const { pins, unplaceable } = useMemo(() => buildMapPins(sources, {
    title: titles.title,
    subtitle: titles.subtitle,
    /**
     * The governed type label, from the relationship's OWN declared attributes.
     *
     * `slot.attributes` carries each attribute's registered vocabulary
     * (`core.relationship_type.attributes_schema` → options with labels), so
     * this reads metadata rather than knowing that an address edge happens to
     * carry a `purpose`. A value with no governed label returns UNDEFINED and
     * the lens then shows no type line at all — an honest gap, never the raw
     * code on somebody's screen.
     */
    typeLabel: (edge) => {
      if (!edge) return undefined
      const stored = edge.attributes && typeof edge.attributes === 'object'
        ? edge.attributes as Record<string, unknown>
        : null
      if (!stored) return undefined
      for (const [slotId, slot] of slotOf) {
        void slotId
        for (const attribute of slot.attributes) {
          if (attribute.options.length === 0) continue      // free text is not a TYPE
          const raw = stored[attribute.key]
          if (raw == null || raw === '') continue
          const option = attribute.options.find((o) => String(o.value) === String(raw))
          if (option) return option.label
        }
      }
      return undefined
    },
    openable: () => !!onOpenRecord,
  }), [sources, titles.title, titles.subtitle, slotOf, onOpenRecord])

  return (
    <>
      {mappable.map(({ slot }) => (
        <SlotRowFeed key={slot.id} slot={slot} recordId={recordId}
          onRows={(rows) => setRowsBySlot((current) =>
            // Identity-stable: writing an equal array on every render of the
            // parent would re-run every memo below it, forever.
            current[slot.id] === rows ? current : { ...current, [slot.id]: rows })} />
      ))}
      <MapLens
        pins={pins}
        unplaceable={unplaceable}
        tiles={tiles}
        heading="Locations"
        /* h2, measured from the page rather than assumed: the relationship
           cascade sits between the record's own h1 and the next h2 on the page,
           so the nearest preceding heading for this lens is that h1. At the
           workspace default of h3 it skipped a level, and axe said so. Its two
           lists then land on h3 — peers of the relationship sections beside
           them, which is what they are. */
        headingLevel={2}
        onOpen={(record) => onOpenRecord?.(record.entity, record.id, record.title)}
        empty="None of these records has a location yet."
        testId="relation-map"
      />
    </>
  )
}

/**
 * One slot's rows, reported upward and rendered nowhere.
 *
 * It exists only because `useRelationRows` is a hook. The read itself is the
 * ordinary governed one every other section makes — this adds no query of its
 * own, which is the property that keeps the map off a private data path.
 */
function SlotRowFeed({ slot, recordId, onRows }: {
  slot: RelationSlot
  recordId: string
  onRows: (rows: readonly { record: Row; edge: Row | null }[]) => void
}) {
  const related = useRelationRows(slot, recordId)
  useEffect(() => { onRows(related.rows) }, [related.rows, onRows])
  return null
}
